import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FloatingCTA from '@/components/feature/FloatingCTA';
import SeoHead from '@/components/feature/SeoHead';
import { teamMembers } from '@/mocks/team';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://vanpolattesisat.biz';

const BIZIM_SCHEMA = [
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Hakkımızda', item: `${SITE_URL}/bizim` },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'Hakkımızda | Van Polat Tesisat',
    description:
      'Van Polat Tesisat hakkında bilgi edinin. 15 yılı aşkın deneyimimiz, profesyonel ekibimiz ve Van\'daki tesisat hizmetlerimiz hakkında her şey.',
    url: `${SITE_URL}/bizim`,
    inLanguage: 'tr-TR',
    mainEntity: {
      '@type': 'LocalBusiness',
      name: 'Van Polat Tesisat',
      foundingDate: '2009',
      numberOfEmployees: { '@type': 'QuantitativeValue', value: 10 },
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Van',
        addressRegion: 'Van',
        addressCountry: 'TR',
      },
      telephone: '+905076074165',
      url: `${SITE_URL}/`,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Van Polat Tesisat',
    url: `${SITE_URL}/`,
    foundingDate: '2009',
    telephone: '+905076074165',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Van',
      addressRegion: 'Van',
      addressCountry: 'TR',
    },
    employee: [
      { '@type': 'Person', name: 'Engin POLAT', jobTitle: 'Tesisat Ustası' },
      { '@type': 'Person', name: 'Hamza POLAT', jobTitle: 'Tesisat Ustası' },
      { '@type': 'Person', name: 'Hayrullah POLAT', jobTitle: 'Tesisat Ustası' },
    ],
  },
];

export default function Bizim() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  const valueIcons = ['ri-shield-check-line', 'ri-user-smile-line', 'ri-medal-line', 'ri-time-line'];
  const values = valueIcons.map((icon, i) => ({ icon, title: t(`val.${i + 1}.title`), desc: t(`val.${i + 1}.desc`) }));

  const stats = [
    { value: t('ap.stat.1.value'), label: t('ap.stat.1.label') },
    { value: t('ap.stat.2.value'), label: t('ap.stat.2.label') },
    { value: t('ap.stat.3.value'), label: t('ap.stat.3.label') },
    { value: t('ap.stat.4.value'), label: t('ap.stat.4.label') },
  ];

  const certs = [1, 2, 3, 4].map((n) => t(`ap.cert.${n}`));

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.05 }
    );
    const items = sectionRef.current?.querySelectorAll('.reveal');
    items?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen flex flex-col pb-16 md:pb-0">
      <SeoHead
        title="Hakkımızda | Van Polat Tesisat"
        description="Van Polat Tesisat olarak 15 yılı aşkın deneyimimizle Van'da güvenilir ve profesyonel tesisat hizmetleri sunuyoruz. Ekibimiz ve değerlerimiz hakkında bilgi alın."
        keywords="van polat tesisat hakkında, van tesisat firması, van profesyonel tesisatçı, van tesisat ekibi"
        canonical="/bizim"
        schema={BIZIM_SCHEMA}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-12 md:pt-40 md:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5a7aac6c847810e07044bf7f7af6b06a.jpg"
            alt={t('ap.hero.title')} className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/75 to-blue-700/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-medium px-4 py-1.5 rounded-full mb-5">
            <div className="w-3 h-3 flex items-center justify-center"><i className="ri-group-line text-blue-200 text-xs"></i></div>
            {t('ap.hero.badge')}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-5" style={{ fontFamily: 'Poppins, sans-serif' }}>{t('ap.hero.title')}</h1>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto">{t('ap.hero.sub')}</p>
        </div>
      </section>

      <div ref={sectionRef} className="pb-16 md:pb-0">
        {/* Story */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
              <div className="reveal order-2 lg:order-1">
                <div className="relative rounded-lg overflow-hidden">
                  <div className="w-full h-80 md:h-96">
                    <img
                      src="https://readdy.ai/api/search-image?query=professional%20plumber%20at%20work%20installing%20pipes%20in%20modern%20bathroom%2C%20blue%20uniform%2C%20copper%20pipes%20and%20fittings%2C%20clean%20white%20tile%20background%2C%20natural%20bright%20lighting%2C%20skilled%20craftsman%20at%20work%2C%20high%20quality%20commercial%20photography&width=800&height=600&seq=about1&orientation=landscape"
                      alt="Vanpolat Tesisat" className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="absolute bottom-4 left-4 bg-blue-600 text-white rounded-lg px-4 py-3">
                    <p className="text-2xl font-bold" style={{ fontFamily: 'Poppins, sans-serif' }}>15+</p>
                    <p className="text-blue-200 text-xs">{t('ap.xp')}</p>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="reveal inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
                  <div className="w-3 h-3 flex items-center justify-center"><i className="ri-history-line text-blue-500"></i></div>
                  {t('ap.story.badge')}
                </div>
                <h2 className="reveal text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-snug" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <strong className="text-blue-600">{t('ap.story.title')}</strong>
                </h2>
                <div className="reveal space-y-4 text-gray-600 text-sm leading-relaxed">
                  <p>{t('ap.story.p1')}</p>
                  <p>{t('ap.story.p2')}</p>
                  <p><strong className="text-gray-900">{t('ap.story.motto')}</strong></p>
                </div>
                <div className="reveal mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {certs.map((cert) => (
                    <div key={cert} className="flex items-center gap-2 text-sm text-gray-700">
                      <div className="w-5 h-5 flex items-center justify-center bg-green-100 rounded-full shrink-0"><i className="ri-check-line text-green-600 text-xs"></i></div>
                      {cert}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 md:py-20 bg-blue-600">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10 text-center">
              {stats.map((stat, idx) => (
                <div key={idx} className="reveal" style={{ animationDelay: `${idx * 0.1}s` }}>
                  <p className="text-3xl md:text-4xl font-bold text-white mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>{stat.value}</p>
                  <p className="text-blue-200 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 md:py-24 bg-blue-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <div className="reveal inline-flex items-center gap-2 bg-white text-blue-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 border border-blue-100">
                <div className="w-3 h-3 flex items-center justify-center"><i className="ri-heart-line text-blue-500"></i></div>
                {t('ap.values.badge')}
              </div>
              <h2 className="reveal text-2xl md:text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {t('ap.values.title')} <span className="text-blue-600">{t('ap.values.highlight')}</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map((v, idx) => (
                <div key={idx} className="reveal bg-white rounded-lg p-6 text-center border border-blue-50 hover:border-blue-200 transition-all" style={{ animationDelay: `${idx * 0.1}s` }}>
                  <div className="w-14 h-14 mx-auto flex items-center justify-center bg-blue-50 rounded-full mb-4">
                    <i className={`${v.icon} text-blue-600 text-2xl`}></i>
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>{v.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <div className="reveal inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
                <div className="w-3 h-3 flex items-center justify-center"><i className="ri-group-line text-blue-500"></i></div>
                {t('ap.team.badge')}
              </div>
              <h2 className="reveal text-2xl md:text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {t('ap.team.title')} <span className="text-blue-600">{t('ap.team.highlight')}</span>
              </h2>
              <p className="reveal text-gray-500 text-sm max-w-lg mx-auto">{t('ap.team.subtitle')}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
              {teamMembers.map((member, idx) => (
                <div key={member.id} className="reveal bg-white rounded-lg overflow-hidden border border-gray-100 hover:border-blue-200 transition-all group" style={{ animationDelay: `${idx * 0.15}s` }}>
                  <div className="w-full h-56 overflow-hidden">
                    <img src={member.avatar} alt={member.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5 text-center">
                    <h3 className="font-bold text-gray-900 text-base mb-0.5" style={{ fontFamily: 'Poppins, sans-serif' }}>{member.name}</h3>
                    <p className="text-blue-600 text-xs font-medium mb-3">{t(`team.role.${member.id}`)}</p>
                    <p className="text-gray-500 text-xs leading-relaxed mb-4">{t(`team.desc.${member.id}`)}</p>
                    <div className="flex gap-2">
                      <a href={member.phoneHref} className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap">
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill text-xs"></i></div>
                        {member.phone}
                      </a>
                      <a href={member.whatsapp} target="_blank" rel="nofollow noreferrer" className="w-10 h-10 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-md transition-colors cursor-pointer" aria-label="WhatsApp">
                        <i className="ri-whatsapp-line text-base"></i>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 md:py-20 bg-blue-900 text-white text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>{t('ap.cta.title')}</h2>
            <p className="text-blue-200 text-base mb-8">{t('ap.cta.sub')}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="tel:+905076074165" className="flex items-center justify-center gap-3 bg-white text-blue-800 hover:bg-blue-50 font-bold text-base px-8 py-4 rounded-md transition-all cursor-pointer whitespace-nowrap">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill text-lg"></i></div>
                0507 607 4165
              </a>
              <a href="https://wa.me/905076074165?text=Merhaba%2C%20tesisat%20konusunda%20yard%C4%B1m%20istiyorum." target="_blank" rel="nofollow noreferrer" className="flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white font-bold text-base px-8 py-4 rounded-md transition-all cursor-pointer whitespace-nowrap">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-whatsapp-line text-lg"></i></div>
                {t('nav.whatsapp')}
              </a>
              <Link to="/randevu" className="flex items-center justify-center gap-3 bg-blue-700 hover:bg-blue-600 border border-blue-500 text-white font-bold text-base px-8 py-4 rounded-md transition-all cursor-pointer whitespace-nowrap">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-calendar-check-line text-lg"></i></div>
                Randevu Al
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />
      <FloatingCTA />
    </div>
  );
}
